import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const choose = async (plan) => {
    if (!user) {
      nav("/register");
      return;
    }
    if (user.subscription_status === "paid") {
      toast.info("You already have an active subscription.");
      return;
    }
    setBusyId(plan.id);
    try {
      await api.post("/subscription/request", { plan_id: plan.id, note: "" });
      await refresh();
      toast.success("Subscription request submitted! An admin will review it shortly.");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="container-page py-12" data-testid="pricing-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink text-center">Choose your plan</h1>
      <p className="mt-3 text-muted2 text-center max-w-2xl mx-auto">
        Payment integration is coming soon. Submit a request and our team will activate your access shortly.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {plans.map((p) => {
          const featured = p.type === "lifetime";
          return (
            <div key={p.id} data-testid={`pricing-card-${p.type}`}
              className={`relative rounded-2xl border ${featured ? "border-moss bg-white shadow-hover" : "border-subtle bg-white shadow-ambient"} p-8 transition-all hover:-translate-y-1 hover:shadow-hover`}>
              {featured && <span className="absolute -top-3 left-6 bg-terra text-white text-xs font-semibold rounded-full px-3 py-1">Best value</span>}
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-moss">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="text-5xl font-heading font-bold text-ink">{p.price}</span>
                <span className="text-sm text-muted2 mb-2">{p.currency} {p.type === "monthly" ? "/ month" : ""}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-muted2">
                {p.benefits?.map((b, i) => (
                  <li key={i} className="flex gap-2"><CheckCircle2 size={16} className="text-leaf mt-0.5 shrink-0" /> {b}</li>
                ))}
              </ul>
              <button onClick={() => choose(p)} disabled={busyId === p.id} data-testid={`choose-plan-${p.type}`}
                className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all active:scale-95 disabled:opacity-60 ${featured ? "bg-terra hover:bg-terra-dark text-white" : "border-2 border-moss text-moss hover:bg-moss hover:text-white"}`}>
                {busyId === p.id && <Loader2 size={16} className="animate-spin" />} Request access
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-soft">
        Once payment integration goes live, this will instantly unlock your account.
      </p>
    </div>
  );
}
